import React from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { reportTypes } from './reportConstants';

const AdvancedReportsGenerateDialog = ({ open, onOpenChange, reportConfig, setReportConfig, generateReport }) => {
  const { t } = useTranslation();
  return (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent className="max-w-2xl">
      <AlertDialogHeader>
        <AlertDialogTitle>{t('reportsComp.genTitle')}</AlertDialogTitle>
        <AlertDialogDescription>{t('reportsComp.genSubtitle')}</AlertDialogDescription>
      </AlertDialogHeader>

      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>{t('reportsComp.typeLabel')}</Label>
          <Select
            value={reportConfig.type_rapport}
            onValueChange={(value) => setReportConfig({ ...reportConfig, type_rapport: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('reportsComp.typePlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {reportTypes.map((type) => (
                <SelectItem key={type.id} value={type.id}>
                  <div className="flex items-center space-x-2">
                    <type.icon className="h-4 w-4" />
                    <span>{t(type.nameKey)}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="date_debut">{t('reportsComp.startDate')}</Label>
            <Input
              id="date_debut"
              type="date"
              value={reportConfig.date_debut}
              onChange={(e) => setReportConfig({ ...reportConfig, date_debut: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="date_fin">{t('reportsComp.endDate')}</Label>
            <Input
              id="date_fin"
              type="date"
              value={reportConfig.date_fin}
              onChange={(e) => setReportConfig({ ...reportConfig, date_fin: e.target.value })}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('reportsComp.formatLabel')}</Label>
            <Select
              value={reportConfig.format_export}
              onValueChange={(value) => setReportConfig({ ...reportConfig, format_export: value })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PDF">PDF</SelectItem>
                <SelectItem value="EXCEL">Excel</SelectItem>
                <SelectItem value="CSV">CSV</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t('reportsComp.periodicityLabel')}</Label>
            <Select
              value={reportConfig.periodicite}
              onValueChange={(value) => setReportConfig({ ...reportConfig, periodicite: value })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="quotidien">{t('reportsComp.daily')}</SelectItem>
                <SelectItem value="hebdomadaire">{t('reportsComp.weekly')}</SelectItem>
                <SelectItem value="mensuel">{t('reportsComp.monthly')}</SelectItem>
                <SelectItem value="personnalise">{t('reportsComp.custom')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Separator />

        <div className="space-y-3">
          <Label>{t('reportsComp.advancedOptions')}</Label>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="inclure_details"
                checked={reportConfig.inclure_details}
                onCheckedChange={(checked) => setReportConfig({ ...reportConfig, inclure_details: checked })}
              />
              <Label htmlFor="inclure_details">{t('reportsComp.includeDetails')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="inclure_graphiques"
                checked={reportConfig.inclure_graphiques}
                onCheckedChange={(checked) => setReportConfig({ ...reportConfig, inclure_graphiques: checked })}
              />
              <Label htmlFor="inclure_graphiques">{t('reportsComp.includeCharts')}</Label>
            </div>
          </div>
        </div>
      </div>

      <AlertDialogFooter>
        <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
        <AlertDialogAction onClick={generateReport} disabled={!reportConfig.type_rapport}>
          <BarChart3 className="mr-2 h-4 w-4" />
          {t('reportsComp.generate')}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
  );
};

export default AdvancedReportsGenerateDialog;
